import { ReactElement } from 'react';
import { useLocation } from 'react-router-dom';

import { configApiRef, useApi } from '@backstage/core-plugin-api';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTitle,
  Banner,
  Flex,
  Text,
  buttonClassNames,
} from '@spotify-internal/encore-web';
import { List, ListRow } from '@spotify-internal/encore-web/next';
import { AuthScreen, useDisclosure } from '@spotify/confidence-core-react';
import { withPageImpression } from '@spotify/plugin-confidence-tracking-react';

import {
  SLACK_SCOPE_LABELS,
  SlackFeatureAvailability,
  SlackInstallResult,
  getFailureCopy,
  getSlackFeatures,
  parseSlackInstallResult,
} from './slackInstallResult';

import styles from './SlackInstallResultPage.module.css';

const PermissionDetails = ({
  missingScopes,
}: Pick<SlackInstallResult, 'missingScopes'>) => {
  const [expanded, { toggle }] = useDisclosure();
  if (!missingScopes.length) return null;
  return (
    <Accordion>
      <AccordionItem>
        <AccordionTitle
          isExpanded={expanded}
          onClick={toggle}
          textVariant="bodyMediumBold"
        >
          Missing permissions ({missingScopes.length})
        </AccordionTitle>
        <AccordionContent isExpanded={expanded}>
          <List role="list">
            {missingScopes.map(scope => (
              <ListRow role="listitem" size="large" height="auto" key={scope}>
                <Flex direction="column" gap="tighter-4">
                  <Text variant="bodySmall">{SLACK_SCOPE_LABELS[scope]}</Text>
                  <Text variant="bodySmall" semanticColor="textSubdued">
                    <code>{scope}</code>
                  </Text>
                </Flex>
              </ListRow>
            ))}
          </List>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

const FeatureList = ({
  title,
  features,
}: {
  title: string;
  features: SlackFeatureAvailability[];
}) => {
  if (!features.length) return null;
  return (
    <section aria-label={title}>
      <Flex direction="column" gap="tighter-2">
        <Text as="h2" variant="titleSmall">
          {title}
        </Text>
        <List role="list">
          {features.map(feature => (
            <ListRow
              role="listitem"
              size="large"
              height="auto"
              key={feature.name}
            >
              <Flex direction="column" gap="tighter-4">
                <Text variant="bodyMediumBold">{feature.name}</Text>
                {feature.available && (
                  <Text as="p" variant="bodySmall" semanticColor="textSubdued">
                    {feature.description}
                  </Text>
                )}
              </Flex>
            </ListRow>
          ))}
        </List>
      </Flex>
    </section>
  );
};

const PermissionGuidance = () => (
  <Flex direction="column" gap="tighter-2">
    <Text as="h2" variant="titleSmall">
      To grant additional permissions
    </Text>
    <Text as="p" variant="bodyMedium">
      Ask your Slack administrator to review the missing permissions. Once
      approved, reconnect Slack to grant them. If Slack cannot send an approval
      request, contact your administrator directly.
    </Text>
  </Flex>
);

type ConnectedResult = Extract<
  SlackInstallResult,
  { status: 'success' | 'partial' }
>;
type FailureResult = Extract<SlackInstallResult, { status: 'failure' }>;

type ResultLinks = {
  integrationHref: string;
  retry?: { href: string; label: string };
};

const getResultLinks = (
  result: SlackInstallResult | null,
  integrationsUrl: string,
): ResultLinks => {
  const account =
    result?.status === 'failure' ? result.requestedAccount : result?.account;
  const integrationParams = new URLSearchParams();
  if (account) {
    integrationParams.set('login', 'true');
    integrationParams.set('organization', account);
  }
  const links: ResultLinks = {
    integrationHref: `/admin/slack${integrationParams.size ? `?${integrationParams}` : ''}`,
  };
  if (
    !result ||
    !account ||
    result.status === 'success' ||
    (result.status === 'failure' &&
      ['maintenance', 'invalid_organization'].includes(result.reason))
  ) {
    return links;
  }
  try {
    const retryUrl = new URL('/slack/install/start', integrationsUrl);
    if (!['http:', 'https:'].includes(retryUrl.protocol)) return links;
    retryUrl.searchParams.set('organization', account);
    links.retry = {
      href: retryUrl.toString(),
      label:
        result.status === 'partial' ||
        (result.status === 'failure' && result.reason === 'missing_scopes')
          ? 'Reconnect Slack'
          : 'Start again',
    };
  } catch {
    return links;
  }
  return links;
};

const ResultHeading = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <Flex direction="column" gap="base" alignItems="center">
    <AuthScreen.Logo />
    <Text as="h1" variant="titleMedium" className={styles.heading}>
      {title}
    </Text>
    <Text as="p" variant="bodyMedium" className={styles.heading}>
      {description}
    </Text>
  </Flex>
);

const ConnectionHeading = ({
  result,
  title,
}: {
  result: ConnectedResult;
  title: string;
}) => (
  <ResultHeading
    title={title}
    description={`${result.workspace} is connected to your Confidence account ${result.account}.`}
  />
);

const SuccessContent = ({ result }: { result: ConnectedResult }) => (
  <ConnectionHeading result={result} title="Slack connected" />
);

const PartialSuccessContent = ({ result }: { result: ConnectedResult }) => {
  const features = getSlackFeatures(result.missingScopes);
  const available = features.filter(feature => feature.available);
  const unavailable = features.filter(feature => !feature.available);
  return (
    <>
      <ConnectionHeading
        result={result}
        title="Slack connected with limited permissions"
      />
      <Text as="p" variant="bodySmall" semanticColor="textSubdued">
        These permissions were checked when you connected. Features also depend
        on your Confidence account settings and access to the relevant Slack
        channels.
      </Text>
      <FeatureList
        title="Available with these permissions"
        features={available}
      />
      <FeatureList
        title="Needs additional permissions"
        features={unavailable}
      />
      {result.missingScopes.includes('chat:write.public') && (
        <Banner colorSet="warning" contextual>
          Invite Confidence to each channel where you want to receive
          notifications.
        </Banner>
      )}
      {unavailable.length === 0 && (
        <Text as="p" variant="bodyMedium">
          The features listed above have the permissions they need. Some
          additional requested permissions were not granted.
        </Text>
      )}
      <PermissionGuidance />
      <PermissionDetails missingScopes={result.missingScopes} />
    </>
  );
};

const FailureContent = ({ result }: { result: FailureResult }) => (
  <>
    <ResultHeading {...getFailureCopy(result)} />
    {result.reason === 'missing_scopes' && <PermissionGuidance />}
    <PermissionDetails missingScopes={result.missingScopes} />
  </>
);

const InvalidResultContent = () => (
  <ResultHeading
    title="No connection result to display"
    description="Open the Slack integration in Confidence to check your connection or start a new connection flow."
  />
);

const ResultContent = ({ result }: { result: SlackInstallResult | null }) => {
  if (!result) return <InvalidResultContent />;
  switch (result.status) {
    case 'success':
      return <SuccessContent result={result} />;
    case 'partial':
      return <PartialSuccessContent result={result} />;
    case 'failure':
      return <FailureContent result={result} />;
    default:
      return <InvalidResultContent />;
  }
};

const ResultActions = ({ links }: { links: ResultLinks }) => (
  <AuthScreen.Actions>
    <a
      className={buttonClassNames({ variant: 'primary' })}
      href={links.integrationHref}
    >
      Open Slack integration
    </a>
    {links.retry && (
      <a
        className={buttonClassNames({ variant: 'secondary-bordered' })}
        href={links.retry.href}
      >
        {links.retry.label}
      </a>
    )}
  </AuthScreen.Actions>
);

export const SlackInstallResultContent = ({
  result,
  integrationsUrl,
}: {
  result: SlackInstallResult | null;
  integrationsUrl: string;
}): ReactElement => (
  <main className={styles.root}>
    <Flex direction="column" alignItems="center" padding="looser">
      <Flex direction="column" gap="looser" className={styles.content}>
        <ResultContent result={result} />
        <ResultActions links={getResultLinks(result, integrationsUrl)} />
      </Flex>
    </Flex>
  </main>
);

const SlackInstallResultPageComponent = () => {
  const { hash } = useLocation();
  const config = useApi(configApiRef);
  return (
    <SlackInstallResultContent
      result={parseSlackInstallResult(hash)}
      integrationsUrl={
        config.getOptionalString('confidence.integrations') ??
        'https://integrations.confidence.dev'
      }
    />
  );
};

export const SlackInstallResultPage = withPageImpression(
  SlackInstallResultPageComponent,
  {
    pageId: 'slack-install-result',
  },
);
